import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { CheckCircle2, XCircle, Upload, FileText, Loader2, Paperclip } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Confirmation() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [answered, setAnswered] = useState(null);
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api
      .get(`/public/confirm/${token}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.student_status) setAnswered(data.student_status);
        setDocs(data.documents || []);
        if (data.doc_categories?.length) setCat(data.doc_categories[0].code);
      })
      .catch((e) => setError(apiError(e.response?.data?.detail) || e.message));
  }, [token]);

  const upload = async (e) => {
    e.preventDefault();
    if (!files.length) {
      toast.error("Choisis au moins un fichier.");
      return;
    }
    const fd = new FormData();
    fd.append("category", cat);
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post(`/public/confirm/${token}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs(data.documents || []);
      setFiles([]);
      toast.success("Documents envoyés.");
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploading(false);
    }
  };

  const answer = async (value) => {
    try {
      await api.post(`/public/confirm/${token}`, { answer: value });
      setAnswered(value);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    const r = params.get("r");
    if (info && !info.student_status && (r === "oui" || r === "non")) answer(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] px-6 py-20">
      <div className="mx-auto max-w-lg rounded-2xl border border-neutral-200 bg-white p-8 sm:p-12">
        <Logo className="h-16" />
        {error && (
          <p className="mt-8 text-red-600" data-testid="confirm-error">
            {error}
          </p>
        )}
        {!error && !info && <p className="mt-8 text-neutral-500">Chargement…</p>}
        {info && answered && (
          <div className="mt-8" data-testid="confirm-answered">
            {answered === "oui" ? (
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            ) : (
              <XCircle className="h-10 w-10 text-neutral-400" />
            )}
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight">Merci pour ta réponse !</h1>
            <p className="mt-4 text-neutral-600">
              {answered === "oui"
                ? "Félicitations et bonne route. Ta réponse est enregistrée."
                : "Pas de souci. Nous restons à ta disposition si tu veux une autre auto-école."}
            </p>
          </div>
        )}
        {info && !answered && (
          <div className="mt-8">
            <h1 className="text-3xl font-extrabold tracking-tight" data-testid="confirm-question">
              {info.first_name}, t'es-tu inscrit(e) chez {info.school_name || "l'auto-école"} ?
            </h1>
            <p className="mt-3 text-neutral-600">Un seul clic, ça nous aide énormément ({info.license_type}).</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => answer("oui")}
                data-testid="confirm-yes-btn"
                className="flex-1 rounded-full bg-brand px-6 py-4 font-bold text-white transition-colors hover:bg-brand-hover"
              >
                Oui, je me suis inscrit(e)
              </button>
              <button
                onClick={() => answer("non")}
                data-testid="confirm-no-btn"
                className="flex-1 rounded-full border border-neutral-300 px-6 py-4 font-bold text-neutral-700 transition-colors hover:border-neutral-900"
              >
                Non, pas encore
              </button>
            </div>
          </div>
        )}
        {info && (
          <div className="mt-10 border-t border-neutral-200 pt-8" data-testid="lead-docs">
            <h2 className="flex items-center gap-2 text-lg font-bold text-neutral-900"><Paperclip className="h-5 w-5 text-brand" /> Mes documents</h2>
            <p className="mt-1 text-sm text-neutral-500">Prépare ton inscription en déposant tes documents (facultatif).</p>
            <form onSubmit={upload} className="mt-4 space-y-3">
              <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="doc-category" className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm">
                {(info.doc_categories || []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
              <input type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="doc-files" className="block w-full text-sm text-neutral-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand/10 file:px-4 file:py-2 file:font-semibold file:text-brand" />
              <button type="submit" disabled={uploading} data-testid="doc-upload-btn" className="inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-neutral-800 disabled:opacity-60">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Envoyer mes documents
              </button>
            </form>
            {docs.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="doc-list">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
                    <FileText className="h-4 w-4 text-neutral-400" /> <span className="truncate">{d.filename}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
